import { z } from 'zod';

import { zoneColorSchema } from './event-space';

/**
 * Turnos dos eventos (docs/eventos, RF-E22 a RF-E26): horas por dia, turnos
 * por zona, de hora certa a hora certa, e as tarefas do evento com zona e hora.
 *
 *   - `/api/site/events/:id/shifts/...`  back-office (`events.manage`)
 *   - `/api/me/event-shifts`              os meus turnos (e os dos meus jovens)
 */

const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'aaaa-mm-dd');
const hourSchema = z.number().int().min(0).max(24);
/** Minutos desde a meia-noite (09:30 = 570). */
const minuteSchema = z.number().int().min(0).max(1439);

/** `staff` = qualquer conta de adulto escolhida pelo gestor. */
export const shiftRoleSchema = z.enum(['volunteer', 'streamer', 'staff']);
export type ShiftRole = z.infer<typeof shiftRoleSchema>;

export const SHIFT_ROLE_LABELS: Record<ShiftRole, string> = {
  volunteer: 'voluntário',
  streamer: 'streamer',
  staff: 'staff',
};

export const shiftCandidateSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  role: shiftRoleSchema,
});
export type ShiftCandidate = z.infer<typeof shiftCandidateSchema>;

export const shiftPersonSchema = shiftCandidateSchema.extend({
  /** Esta pessoa tem outro turno que se sobrepoe a este. */
  overlap: z.boolean(),
});
export type ShiftPerson = z.infer<typeof shiftPersonSchema>;

export const eventShiftSchema = z.object({
  id: z.string().uuid(),
  zoneId: z.string().uuid(),
  day: z.string(),
  startHour: z.number().int(),
  endHour: z.number().int(),
  needed: z.number().int(),
  note: z.string(),
  people: z.array(shiftPersonSchema),
  /** Menos pessoas do que as precisas ("Por preencher"). */
  unfilled: z.boolean(),
});
export type EventShift = z.infer<typeof eventShiftSchema>;

export const shiftDaySchema = z.object({
  day: z.string(),
  openHour: z.number().int(),
  closeHour: z.number().int(),
  /** As horas foram mudadas a mao (sem isto, vem do inicio e do fim do evento). */
  custom: z.boolean(),
  summary: z.object({
    volunteersConfirmed: z.number().int(),
    volunteersWithShift: z.number().int(),
    unfilled: z.number().int(),
    /** Pessoas com dois turnos a mesma hora neste dia. */
    overlaps: z.number().int(),
    /** Tarefas sem ninguem: nem pessoa escolhida nem turno nessa zona a essa hora. */
    tasksUncovered: z.number().int(),
  }),
  /** Voluntarios confirmados neste dia sem turno nenhum. */
  withoutShift: z.array(shiftCandidateSchema),
});
export type ShiftDay = z.infer<typeof shiftDaySchema>;

/** Uma tarefa do evento (RF-E26): uma marca na grelha, numa zona e a uma hora. */
export const eventTaskSchema = z.object({
  id: z.string().uuid(),
  zoneId: z.string().uuid(),
  day: z.string(),
  atMinute: z.number().int(),
  title: z.string(),
  /** Escolhida a mao; sem ela, a tarefa e de quem esta de turno. */
  assignee: shiftCandidateSchema.nullable(),
  /** Logins de quem esta de turno nessa zona a essa hora. */
  onShift: z.array(z.string()),
  /** Ninguem: nem pessoa escolhida nem ninguem de turno. */
  uncovered: z.boolean(),
  done: z.boolean(),
  doneBy: z.string().nullable(),
});
export type EventTask = z.infer<typeof eventTaskSchema>;

export const eventShiftsSchema = z.object({
  eventId: z.string().uuid(),
  zones: z.array(z.object({ id: z.string().uuid(), name: z.string(), color: zoneColorSchema })),
  days: z.array(shiftDaySchema),
  shifts: z.array(eventShiftSchema),
  tasks: z.array(eventTaskSchema),
});
export type EventShifts = z.infer<typeof eventShiftsSchema>;

export const dayHoursInputSchema = z
  .object({ day: daySchema, openHour: hourSchema, closeHour: hourSchema })
  .refine((d) => d.openHour < d.closeHour, {
    message: 'o fecho tem de ser depois da abertura',
    path: ['closeHour'],
  });
export type DayHoursInput = z.infer<typeof dayHoursInputSchema>;

export const eventShiftInputSchema = z
  .object({
    zoneId: z.string().uuid(),
    day: daySchema,
    startHour: hourSchema,
    endHour: hourSchema,
    needed: z.number().int().min(0).max(50).default(1),
    note: z.string().trim().max(200).default(''),
    accountIds: z.array(z.string().uuid()).max(50).default([]),
    /**
     * `false` ao arrastar o turno na grelha: mudar a zona ou as horas nao manda
     * email a quem ja la estava (decidido pelo GrowuP a 26-10-2026).
     */
    notify: z.boolean().default(true),
  })
  .refine((s) => s.startHour < s.endHour, {
    message: 'o fim tem de ser depois do inicio',
    path: ['endHour'],
  });
export type EventShiftInput = z.input<typeof eventShiftInputSchema>;

export const eventTaskInputSchema = z.object({
  zoneId: z.string().uuid(),
  day: daySchema,
  atMinute: minuteSchema,
  title: z.string().trim().min(1, 'falta o titulo').max(140),
  assigneeAccountId: z.string().uuid().nullable().default(null),
});
export type EventTaskInput = z.input<typeof eventTaskInputSchema>;

export const taskDoneInputSchema = z.object({ done: z.boolean() });
export type TaskDoneInput = z.infer<typeof taskDoneInputSchema>;

/** Copiar os turnos e as tarefas de outro evento, sem pessoas. */
export const shiftsCopyInputSchema = z.object({ fromEventId: z.string().uuid() });
export type ShiftsCopyInput = z.infer<typeof shiftsCopyInputSchema>;

export const shiftsCopyResultSchema = z.object({
  shifts: eventShiftsSchema,
  copied: z.object({ shifts: z.number().int(), tasks: z.number().int() }),
  /** Turnos e tarefas sem zona com o mesmo nome, ou num dia que este evento nao tem. */
  skipped: z.number().int(),
});
export type ShiftsCopyResult = z.infer<typeof shiftsCopyResultSchema>;

/** Um turno meu (ou de um jovem meu) em `growup.gg/conta/eventos`. */
export const myEventShiftSchema = z.object({
  id: z.string().uuid(),
  day: z.string(),
  startHour: z.number().int(),
  endHour: z.number().int(),
  zoneName: z.string(),
  zoneColor: zoneColorSchema,
  note: z.string(),
  youthLogin: z.string().nullable(),
  event: z.object({ slug: z.string(), title: z.string() }),
});
export type MyEventShift = z.infer<typeof myEventShiftSchema>;

/** Uma tarefa do evento a meu cargo (ou de um jovem meu): no meu turno ou escolhida para mim. */
export const myShiftTaskSchema = z.object({
  id: z.string().uuid(),
  /** O turno onde cai; `null` se foi escolhida para mim fora dos meus turnos. */
  shiftId: z.string().uuid().nullable(),
  day: z.string(),
  atMinute: z.number().int(),
  title: z.string(),
  zoneName: z.string(),
  done: z.boolean(),
  youthLogin: z.string().nullable(),
  event: z.object({ slug: z.string(), title: z.string() }),
});
export type MyShiftTask = z.infer<typeof myShiftTaskSchema>;

export const myEventShiftsSchema = z.object({
  shifts: z.array(myEventShiftSchema),
  tasks: z.array(myShiftTaskSchema),
});
export type MyEventShifts = z.infer<typeof myEventShiftsSchema>;

/** `9h–13h`. */
export const hoursLabel = (start: number, end: number) => `${String(start)}h–${String(end)}h`;

/** `09:30`. */
export const minuteLabel = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
